<template lang="pug">
    Tabs.google-tag-manager
        template(slot="tabbar-buttons")
            el-tooltip(class="item" effect="dark" :content="$t('Dismiss')" placement="top")
                el-button(
                    circle
                    icon="el-icon-fa-times fas"
                    :disabled="saving"
                    @click="$router.back()"
                )
            el-tooltip(class="item" effect="dark" :content="$t('Save')" placement="top")
                el-button(
                    circle
                    type="success"
                    icon="el-icon-fa-save fas"
                    :loading="saving"
                    :disabled="saving"
                    @click="submit"
                )

        el-form(
            label-position="top"
            :model="store.form"
            :rules="store.rules"
            ref="form"
        )
            el-card(shadow="always" v-loading="store.loading")
                .section
                    .flex-row
                        el-form-item.grow(:label="$t('Container ID')" prop="container_id")
                            el-input(v-model="store.form.container_id" maxlength="16" placeholder="GTM-XXXXXXX")

                    .switches-row
                        el-form-item(class="switch")
                            el-switch(
                                :active-text="$t('Enabled')"
                                v-model="store.form.is_enabled"
                            )
                        el-form-item(class="switch")
                            .switch-with-help
                                el-switch(
                                    :active-text="$t('Send customer data')"
                                    v-model="store.form.send_user_data"
                                )
                                el-popover(placement="top" width="340" trigger="hover")
                                    .user-data-popover
                                        p {{ $t('Adds the email address, name, phone number and billing address to the purchase event so Google can match the sale to an ad click. Google calls this "enhanced conversions".') }}
                                        ul
                                            li {{ $t('Readable by every other tag in your container, not only by Google.') }}
                                            li {{ $t('Sent only after cookie consent, and only on the order confirmation.') }}
                                            li {{ $t('Needs cover in your privacy policy and a legal basis.') }}
                                        p.user-data-popover__verdict {{ $t('Useful with Google Ads. Not needed for Analytics statistics.') }}
                                    el-button.switch-help(slot="reference" circle size="mini") ?

                    p.help {{ $t('The container is only loaded after the visitor accepts cookies. Nothing is sent to Google before that.') }}

                    p.help.is-warning(v-if="store.form.send_user_data")
                        i.el-icon-fa-triangle-exclamation.fas
                        |
                        | {{ $t('Customer data is passed to Google with every order. Check your privacy policy and legal basis.') }}

                .section
                    h4.section-title {{ $t('Events') }}
                    .event-groups
                        .event-group(v-for="group in eventGroups" :key="group.title")
                            h5.event-group-title {{ group.title }}
                            .event(v-for="event in group.events" :key="event.key")
                                el-switch(:active-text="event.label" v-model="store.form[event.key]")
                                code.event-name {{ event.name }}

                el-alert(type="info" :closable="false" show-icon)
                    template(slot="title")
                        span {{ $t('Configure tags and triggers in') }}
                        |
                        a(href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer") {{ $t('Google Tag Manager') }}
                        | . {{ $t('The grey name under each event is its GA4 dataLayer event name - use it as the trigger in your container.') }}

                el-alert(type="warning" :closable="false" show-icon)
                    template(slot="title")
                        span {{ $t('If you already pasted a Tag Manager snippet into the custom scripts setting, remove it. Two containers on one page duplicate every event.') }}

                el-collapse.setup-guide
                    el-collapse-item(:title="$t('Setup instructions')" name="setup")
                        ol.setup-steps
                            li
                                strong {{ $t('Get the container ID') }}
                                p
                                    | {{ $t('Open') }}
                                    |
                                    a(href="https://tagmanager.google.com/" target="_blank" rel="noopener noreferrer") tagmanager.google.com
                                    | . {{ $t('Create a container of type "Web" for this shop, or open the existing one. The ID looks like GTM-XXXXXXX and is shown next to the container name.') }}
                                p.setup-note {{ $t('Careful: Google Ads is a different product. A container ID is never issued there.') }}
                            li
                                strong {{ $t('Connect it here') }}
                                p {{ $t('Paste the ID above, switch "Enabled" on and save. Nothing else is needed to start collecting data.') }}
                            li
                                strong {{ $t('Check it works') }}
                                p
                                    | {{ $t('Open your shop and accept the cookie banner, then start preview mode in') }}
                                    |
                                    a(href="https://tagassistant.google.com/" target="_blank" rel="noopener noreferrer") {{ $t('Tag Assistant') }}
                                    | . {{ $t('You should see the events listed above appear as you browse.') }}
                                p.setup-note {{ $t('Accept the cookies BEFORE starting preview mode, in the same browser. Without consent the container does not exist yet, so Tag Assistant reports "not connected". If that happened, accept and start the preview again.') }}
                            li
                                strong {{ $t('Measure conversions (optional)') }}
                                p {{ $t('This page only delivers the data. To record conversions in Google Ads or Analytics, add the matching tag inside Tag Manager and trigger it on the event name shown under each toggle, for example "purchase".') }}
                                p.setup-note {{ $t('Enhanced conversions additionally need customer data. Switch on "Send customer data" above and read the note that appears before you do.') }}

            .form-buttons
                el-button(
                    round
                    icon="el-icon-fa-times fas"
                    :disabled="saving"
                    @click="$router.back()"
                ) {{ $t('Dismiss') }}
                el-button(
                    round
                    type="success"
                    icon="el-icon-fa-save fas"
                    :loading="saving"
                    :disabled="saving"
                    @click="submit"
                ) {{ $t('Save') }}
</template>

<i18n>
{
    "de": {
        "Container ID": "Container-ID",
        "Enabled": "Aktiviert",
        "Send customer data": "Kundendaten senden",
        "Discovery": "Entdeckung",
        "Product & Cart": "Produkt & Warenkorb",
        "Checkout": "Checkout",
        "The container is only loaded after the visitor accepts cookies. Nothing is sent to Google before that.": "Der Container wird erst geladen, nachdem der Besucher Cookies akzeptiert hat. Vorher wird nichts an Google übertragen.",
        "Events": "Events",
        "Page View": "Seitenaufruf",
        "View Item": "Produkt angesehen",
        "View Item List": "Produktliste angesehen",
        "Select Item": "Produkt ausgewählt",
        "Add to Cart": "In den Warenkorb",
        "Remove from Cart": "Aus dem Warenkorb entfernt",
        "View Cart": "Warenkorb angesehen",
        "Begin Checkout": "Checkout gestartet",
        "Add Shipping Info": "Versandart gewählt",
        "Add Payment Info": "Zahlungsart gewählt",
        "Purchase": "Kauf",
        "Search": "Suche",
        "Configure tags and triggers in": "Tags und Trigger konfigurieren im",
        "Google Tag Manager": "Google Tag Manager",
        "The grey name under each event is its GA4 dataLayer event name - use it as the trigger in your container.": "Der graue Name unter jedem Event ist der GA4-dataLayer-Eventname - damit wird der Trigger im Container erstellt.",
        "If you already pasted a Tag Manager snippet into the custom scripts setting, remove it. Two containers on one page duplicate every event.": "Falls bereits ein Tag-Manager-Snippet in den eigenen Skripten hinterlegt ist, bitte entfernen. Zwei Container auf einer Seite verdoppeln jedes Event.",
        "Setup instructions": "Einrichtung",
        "Get the container ID": "Container-ID holen",
        "Open": "Öffnen Sie",
        "Create a container of type \"Web\" for this shop, or open the existing one. The ID looks like GTM-XXXXXXX and is shown next to the container name.": "Legen Sie dort einen Container vom Typ \"Web\" für diesen Shop an oder öffnen Sie den vorhandenen. Die ID sieht aus wie GTM-XXXXXXX und steht neben dem Container-Namen.",
        "Careful: Google Ads is a different product. A container ID is never issued there.": "Achtung: Google Ads ist ein anderes Produkt. Eine Container-ID gibt es dort nicht.",
        "Connect it here": "Hier verbinden",
        "Paste the ID above, switch \"Enabled\" on and save. Nothing else is needed to start collecting data.": "ID oben einfügen, \"Aktiviert\" einschalten und speichern. Mehr ist nicht nötig, damit Daten erfasst werden.",
        "Check it works": "Funktion prüfen",
        "Open your shop and accept the cookie banner, then start preview mode in": "Shop öffnen, Cookie-Hinweis akzeptieren und dann den Vorschaumodus starten im",
        "Tag Assistant": "Tag Assistant",
        "You should see the events listed above appear as you browse.": "Beim Klicken durch den Shop erscheinen die oben aufgeführten Events.",
        "Measure conversions (optional)": "Conversions messen (optional)",
        "This page only delivers the data. To record conversions in Google Ads or Analytics, add the matching tag inside Tag Manager and trigger it on the event name shown under each toggle, for example \"purchase\".": "Diese Seite liefert nur die Daten. Um Conversions in Google Ads oder Analytics zu erfassen, legen Sie den passenden Tag im Tag Manager an und lösen ihn über den Eventnamen unter dem jeweiligen Schalter aus, zum Beispiel \"purchase\".",
        "Enhanced conversions additionally need customer data. Switch on \"Send customer data\" above and read the note that appears before you do.": "Erweiterte Conversions benötigen zusätzlich Kundendaten. Schalten Sie oben \"Kundendaten senden\" ein und lesen Sie vorher den eingeblendeten Hinweis.",
        "Dismiss": "Abbrechen",
        "Save": "Speichern",
        "Form is not valid!": "Formular ist ungültig!",
        "Correct your input and submit again.": "Bitte Eingabe korrigieren und erneut speichern.",
        "Google Tag Manager saved successfully!": "Google Tag Manager erfolgreich gespeichert!",
        "Google Tag Manager could not be saved.": "Google Tag Manager konnte nicht gespeichert werden.",
        "Adds the email address, name, phone number and billing address to the purchase event so Google can match the sale to an ad click. Google calls this \"enhanced conversions\".": "Hängt E-Mail-Adresse, Name, Telefonnummer und Rechnungsadresse an das Purchase-Event, damit Google den Kauf einem Anzeigenklick zuordnen kann. Google nennt das \"erweiterte Conversions\".",
        "Readable by every other tag in your container, not only by Google.": "Für jeden weiteren Tag in Ihrem Container lesbar, nicht nur für Google.",
        "Sent only after cookie consent, and only on the order confirmation.": "Wird erst nach der Cookie-Zustimmung gesendet und nur bei der Bestellbestätigung.",
        "Needs cover in your privacy policy and a legal basis.": "Erfordert eine Regelung in der Datenschutzerklärung und eine Rechtsgrundlage.",
        "Useful with Google Ads. Not needed for Analytics statistics.": "Sinnvoll mit Google Ads. Für Analytics-Statistiken nicht nötig.",
        "Customer data is passed to Google with every order. Check your privacy policy and legal basis.": "Kundendaten werden bei jeder Bestellung an Google übergeben. Datenschutzerklärung und Rechtsgrundlage prüfen.",
        "Accept the cookies BEFORE starting preview mode, in the same browser. Without consent the container does not exist yet, so Tag Assistant reports \"not connected\". If that happened, accept and start the preview again.": "Cookies VOR dem Start des Vorschaumodus akzeptieren, im selben Browser. Ohne Zustimmung existiert der Container noch nicht, daher meldet der Tag Assistant \"nicht verbunden\". Falls das passiert ist: akzeptieren und die Vorschau erneut starten."
    }
}
</i18n>

<script>
import Tabs from '@/views/cms/Marketing/Partials/Tabs';
import { useMarketingGoogleTagManagerStore } from '@/stores';

export default {
    name: 'GoogleTagManagerForm',

    components: {
        Tabs,
    },

    data() {
        return {
            saving: false,
        };
    },

    computed: {
        store() {
            return useMarketingGoogleTagManagerStore();
        },

        // Grouped by customer journey stage rather than listed flat: it fills the
        // card width, and the stage tells an admin what an event like "select_item"
        // actually means. The dataLayer name is shown because that is the string
        // you need to build a trigger in Tag Manager.
        eventGroups() {
            return [
                {
                    title: this.$t('Discovery'),
                    events: [
                        { key: 'track_page_view', label: this.$t('Page View'), name: 'page_view' },
                        { key: 'track_view_item_list', label: this.$t('View Item List'), name: 'view_item_list' },
                        { key: 'track_select_item', label: this.$t('Select Item'), name: 'select_item' },
                        { key: 'track_search', label: this.$t('Search'), name: 'search' },
                    ],
                },
                {
                    title: this.$t('Product & Cart'),
                    events: [
                        { key: 'track_view_item', label: this.$t('View Item'), name: 'view_item' },
                        { key: 'track_add_to_cart', label: this.$t('Add to Cart'), name: 'add_to_cart' },
                        { key: 'track_remove_from_cart', label: this.$t('Remove from Cart'), name: 'remove_from_cart' },
                        { key: 'track_view_cart', label: this.$t('View Cart'), name: 'view_cart' },
                    ],
                },
                {
                    title: this.$t('Checkout'),
                    events: [
                        { key: 'track_begin_checkout', label: this.$t('Begin Checkout'), name: 'begin_checkout' },
                        { key: 'track_add_shipping_info', label: this.$t('Add Shipping Info'), name: 'add_shipping_info' },
                        { key: 'track_add_payment_info', label: this.$t('Add Payment Info'), name: 'add_payment_info' },
                        { key: 'track_purchase', label: this.$t('Purchase'), name: 'purchase' },
                    ],
                },
            ];
        },
    },

    created() {
        this.store.fetch().then(() => {
            // Without this the required rule fires red immediately after
            // hydrating a record that has never been saved.
            this.$nextTick(() => {
                if (this.$refs.form) {
                    this.$refs.form.clearValidate();
                }
            });
        });
    },

    methods: {
        submit() {
            this.$refs.form.validate((valid) => {
                if (!valid) {
                    this.$notify.warning({
                        title: this.$t('Form is not valid!'),
                        message: this.$t('Correct your input and submit again.'),
                        iconClass: 'fas el-icon-fa-exclamation-circle',
                        position: 'bottom-left',
                        customClass: 'dark',
                        duration: 8000,
                    });
                    return;
                }
                this.saving = true;
                this.store.save()
                    .then(() => {
                        this.$notify.success({
                            title: this.$t('Google Tag Manager saved successfully!'),
                            iconClass: 'fas el-icon-fa-check',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 4000,
                        });
                    })
                    .catch(() => {
                        this.$notify.error({
                            title: this.$t('Google Tag Manager could not be saved.'),
                            iconClass: 'fas el-icon-fa-xmark',
                            position: 'bottom-left',
                            customClass: 'dark',
                            duration: 8000,
                        });
                    })
                    .finally(() => {
                        this.saving = false;
                    });
            });
        },
    },
};
</script>

<style lang="sass" scoped>
.google-tag-manager
    .section
        &:not(:last-child)
            margin-bottom: 24px
    .section-title
        color: $color-grey-text
        margin: 0
        font-size: 13px
        font-weight: 600
        text-transform: uppercase
        letter-spacing: .5px
    .flex-row
        display: flex
        align-items: flex-end
        gap: 1rem
        .grow
            flex-grow: 1
    .switches-row
        display: flex
        align-items: center
        gap: 2rem
        margin-top: 4px
        .el-form-item
            margin-bottom: 0
    .help
        color: $color-grey-text
        font-size: 12px
        line-height: 1.4
        margin: 8px 0 0

    // Three journey stages side by side, so the toggles use the full card width
    // instead of stacking into the left third.
    .switch-with-help
        display: flex
        align-items: center
        gap: 8px
    // Sizing copied from AiDisclosureField's __help, including the !important:
    // globals.sass sets `body .el-button.is-circle.el-button--mini { min-width: 28px }`,
    // which stretches a 20px button into an oval without it.
    .switch-help
        padding: 0
        width: 20px
        height: 20px
        min-height: auto
        min-width: auto !important
        font-size: 11px
        font-weight: 600
        line-height: 1
        color: $color-grey-text
    .help
        &.is-warning
            color: #E6A23C
            i
                margin-right: 4px
    .event-groups
        display: grid
        grid-template-columns: repeat(3, minmax(0, 1fr))
        gap: 8px 32px
        margin-top: 16px
    .event-group-title
        color: $color-grey-text
        margin: 0 0 12px
        padding-bottom: 8px
        border-bottom: 1px solid #EBEEF5
        font-size: 11px
        font-weight: 600
        text-transform: uppercase
        letter-spacing: .6px
    .event
        margin-bottom: 14px
        &:last-child
            margin-bottom: 0
    .event-name
        display: block
        // Aligns under the switch label: 40px switch + 10px Element gap.
        padding-left: 50px
        margin-top: 1px
        color: $color-grey-text
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace
        font-size: 11px
        line-height: 1.3

    @media (max-width: 1400px)
        .event-groups
            grid-template-columns: repeat(2, minmax(0, 1fr))
            gap: 20px 32px
    @media (max-width: 900px)
        .event-groups
            grid-template-columns: minmax(0, 1fr)
    .el-alert
        margin-top: 16px
        a
            color: #409EFF
            text-decoration: underline
            &:before
                content: ' '
    .setup-guide
        margin-top: 8px
        border-top: none
        border-bottom: none
        // ::v-deep is required: these are inner nodes of ElCollapseItem, so the
        // scoped attribute never lands on them and globals.sass wins instead
        // (`.el-collapse-item__header { font-size: 1rem }`).
        ::v-deep .el-collapse-item__header
            font-size: 13px
            font-weight: 600
            color: $color-grey-text
            text-transform: uppercase
            letter-spacing: .5px
        ::v-deep .el-collapse-item__content
            padding-bottom: 12px
    .setup-steps
        margin: 0
        padding-left: 18px
        color: $color-grey-text
        font-size: 13px
        line-height: 1.5
        li
            margin-bottom: 14px
            &:last-child
                margin-bottom: 0
        strong
            color: #303133
        p
            margin: 2px 0 0
        a
            color: #409EFF
            text-decoration: underline
    .setup-note
        font-style: italic
        opacity: .85
    .form-buttons
        margin-top: 16px
        display: flex
        justify-content: flex-end
        gap: 8px

// el-popover content is moved to <body>, so these live at the top level: the scoped
// attribute still matches it, an ancestor selector would not.
.user-data-popover
    font-size: 13px
    line-height: 1.5
    p
        margin: 0 0 8px
    ul
        margin: 0 0 8px
        padding-left: 18px
    li
        margin-bottom: 3px
    &__verdict
        margin-bottom: 0
        font-weight: 600
</style>
